import { useCallback, useEffect, useMemo, useState } from 'react';
import { notification, Upload, UploadProps } from 'antd';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { generateKSUID } from '@shared/utils/ksuid-generator';
import { useImportStore } from '@shared/store/import-store';
import { ImportAPIResponse } from '@modules/contacts/contacts.types';
import { AxiosMediaUploadInstance } from '@/lib/axios/axios-media-upload-instance';
import { useMedia } from '@shared/hooks/use-media';
import { useTranslation } from 'react-i18next';
import { isValidFileSize } from '@shared/utils/is-valid-file-size';

export type UseImportType = {
  limit?: number;
};

export type FileUploadResponse = {
  message: string;
  data: {
    id: number;
    originalname: string;
    mimetype: string;
    size: number;
    key: string;
    url: string;
    workspaceId: number;
    log: {
      acl: string;
      key: string;
      etag: string;
      size: number;
      bucket: string;
      encoding: string;
      location: string;
      metadata: {
        encoding: string;
        mimetype: string;
        fieldname: string;
        originalname: string;
      };
      mimetype: string;
      fieldname: string;
      versionId: string;
      contentType: string;
      originalname: string;
      storageClass: string;
      contentEncoding: string | null;
      contentDisposition: string | null;
      serverSideEncryption: string | null;
    };
    bucket: string;
    retentionPolicy: string | null;
    updatedAt: string;
    createdAt: string;
    avatar: string | null;
    deletedAt: string | null;
  };
};

export interface ViewableFile extends RcFile {
  preview?: FileUploadResponse;
}

export function useUploadMultiMedia({ limit = 1 }: UseImportType) {
  const accept = '.jpg,.jpeg,.png,.pdf,.heic,.heif';
  const VALID_FILE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'] as const;

  const { setReferenceId } = useImportStore();
  const [importFiles, setImportFiles] = useState<ViewableFile[]>([]);
  const { uploadMedia, loading } = useMedia<ImportAPIResponse>('/upload/api/v1/media/single');
  const [tempFileList, setTempFileList] = useState<ViewableFile[]>([]);
  const { t } = useTranslation();

  const importBeforeUpload: UploadProps['beforeUpload'] = (file, fileList) => {
    if (!isValidFileSize(file, 5)) {
      notification.error({
        message: t('file_size_exceeds_limit'),
        description: t('file_size_must_be_smaller_than_5mb'),
        duration: 5,
      });
      return false;
    }

    if (!VALID_FILE_TYPES.includes(file.type as (typeof VALID_FILE_TYPES)[number])) {
      notification.error({ message: t('please_upload_a_valid_file_type') });
      return false;
    }

    if (limit !== 1 && importFiles.length + fileList.length > limit) {
      notification.error({
        message: t('file_upload_limit'),
        description: t('you_can_only_upload_up_to_{limit}_files', { limit: limit }),
      });
      return Upload.LIST_IGNORE;
    }

    setImportFiles([...importFiles, ...fileList]);
    return false;
  };

  const onRemoveImportFile = (rc: UploadFile<ViewableFile>) => {
    setImportFiles((files) => files.filter((file) => file !== rc));
    return true;
  };

  const handleFileImport = async (file: File) => {
    const ksuid = generateKSUID();
    if (!ksuid) notification.error({ message: t('import_failed') });
    setReferenceId(ksuid || '');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('referenceId', ksuid || '');

    uploadMedia(formData)
      .then(() => {
        closeImportModal();
      })
      .catch(() => {
        notification.error({ message: t('import_failed') });
      });
  };

  const handleConfirmUpload = useCallback(async () => {
    if (!importFiles?.length) return;
    await handleFileImport(importFiles[0]);
  }, [importFiles]);

  const handleRemoveClick = (file: UploadFile) => {
    const rc = (file.originFileObj as UploadFile<ViewableFile>) || (file as unknown as UploadFile<ViewableFile>);
    onRemoveImportFile(rc);
  };

  const closeImportModal = () => {
    setImportFiles([]);
  };

  const fileList: UploadFile<ViewableFile>[] = useMemo(
    () =>
      (importFiles || []).map((f) => ({
        uid: f.uid,
        name: f.name,
        size: f.size,
        originFileObj: f,
      })),
    [importFiles]
  );

  useEffect(() => {
    async function getPreviewFileLists() {
      const updatedFiles: ViewableFile[] = [];
      const uploadPromises = importFiles?.map(async (file) => {
        try {
          const formData = new FormData();
          formData.set('file', file);
          const body = formData;
          const response = await AxiosMediaUploadInstance.post('/upload/api/v1/media/single', body, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          file.preview = response?.data;
          updatedFiles?.push(file);
          return response?.data?.data;
        } catch {
          notification.warning({ message: t('there_was_an_error_uploading_your_file_try_again') });
          setImportFiles([]);
        }
      });

      const results = await Promise.all(uploadPromises);
      setTempFileList(updatedFiles);
      return results;
    }

    getPreviewFileLists();
  }, [importFiles]);

  const uploadProps: UploadProps = useMemo(
    () => ({
      name: 'file',
      accept,
      multiple: limit !== 1,
      showUploadList: false,
      beforeUpload: importBeforeUpload,
      fileList,
      onRemove: onRemoveImportFile,
      disabled: loading || fileList.length == limit,
    }),
    [loading, importBeforeUpload, fileList, onRemoveImportFile]
  );

  return {
    handleRemoveClick,
    uploadProps,
    fileList,
    tempFileList,
    handleConfirmUpload,
    loading,
    closeImportModal,
  };
}

export type UseUploadMediaReturn = ReturnType<typeof useUploadMultiMedia>;
